import { defineStore } from "pinia";
import { PriceConfiguration } from "./entity";
import { PriceConfigurationList } from "./list.gen";
import type {
  PriceConfigurationStore,
  PriceConfigurationStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const priceConfigurationList = new PriceConfigurationList();
export const usePriceConfigurationStore = defineStore(
  "priceConfigurationStore",
  {
    state: (): PriceConfigurationStoreState => ({
      activeID: null,
      cache: {},
      list: [] as PriceConfiguration[],
      retrievalTimestamp: 0,
      updateKey: 0,
      storeUpdateTimeout: null,
    }),
    actions: {
      initializeStore(): void {
        this.convertList();
      },
      emitFormUpdateEvent(id: string): void {
        window.dispatchEvent(
          new CustomEvent("form-update-PriceConfiguration", { detail: { id } }),
        );
      },
      emitStoreUpdateEvent(): void {
        // Clear existing timeout
        if (this.storeUpdateTimeout !== null) {
          clearTimeout(this.storeUpdateTimeout);
        }

        // Set new timeout
        this.storeUpdateTimeout = setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("store.update.PriceConfiguration"),
          );
          this.storeUpdateTimeout = null;
        }, 1500);
      },
      async activate(priceConfiguration: PriceConfiguration): Promise<void> {
        // Set active
        await priceConfiguration
          .activate(auth.firebaseUser?.uid)
          .then(() => {
            this.activeID = priceConfiguration.id;
          })
          .catch((error) => {
            console.error(error);
            this.activeID = null;
          });
      },
      add(priceConfiguration: PriceConfiguration): void {
        const id = priceConfiguration.id;
        if (typeof id === "string" && !this.contains(id)) {
          this.list.push(priceConfiguration);

          // Set active
          if (this.active === null) {
            this.activeID = priceConfiguration.id;
          }
        } else {
          this.reinsert(priceConfiguration);
        }
        this.updateKey += 1;
      },
      convertList(): void {
        this.list = (this.list as PriceConfiguration[]).map((x: any) => {
          const priceConfiguration = new PriceConfiguration({});
          priceConfiguration.set(x as JSONData);
          return priceConfiguration;
        });
      },
      async delete(priceConfiguration: PriceConfiguration): Promise<void> {
        await priceConfiguration
          .delete()
          .then(() => {
            this.remove(priceConfiguration);
          })
          .finally(() => {
            this.emitStoreUpdateEvent();
          });
      },
      async deleteById(id: string): Promise<void> {
        const priceConfiguration = (this.list as PriceConfiguration[]).find(
          (x: PriceConfiguration) => x.id === id,
        );
        if (priceConfiguration !== undefined) {
          await this.delete(priceConfiguration);
        } else {
          throw new Error("Could not find PriceConfiguration with id: " + id);
        }
      },
      contains(id: string): boolean {
        return (this.list as PriceConfiguration[]).some(
          (x: PriceConfiguration) => x.id === id,
        );
      },
      get(id: string): PriceConfiguration | null {
        return (
          (this.list as PriceConfiguration[]).find(
            (x: PriceConfiguration) => x.id === id,
          ) || null
        );
      },
      reinsert(priceConfiguration: PriceConfiguration): void {
        const index = (this.list as PriceConfiguration[]).findIndex(
          (x: PriceConfiguration) => x.id === priceConfiguration.id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            priceConfiguration,
            ...this.list.slice(index + 1),
          ];
        } else {
          this.add(priceConfiguration);
        }
        this.updateKey += 1;
      },
      remove({ id }: PriceConfiguration): void {
        const index = (this.list as PriceConfiguration[]).findIndex(
          (x: PriceConfiguration) => x.id === id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            ...this.list.slice(index + 1),
          ];
        }
        this.updateKey += 1;
      },
      async retrieve(id: string, force = false): Promise<PriceConfiguration> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          const priceConfiguration = this.get(id);
          if (priceConfiguration !== null) {
            return priceConfiguration;
          }
        }

        // Retrieve priceConfiguration
        const priceConfiguration = new PriceConfiguration({});

        // Get priceConfiguration
        await priceConfiguration.get([id]).then(async (entity) => {
          this.reinsert(entity);

          // Activate priceConfiguration
          await this.activate(entity);
        });

        return priceConfiguration;
      },
      async retrieveAll(force = false): Promise<{
        active: string | null;
        items: PriceConfiguration[];
      }> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          return {
            active: this.activeID,
            items: this.list as PriceConfiguration[],
          };
        }

        // Clear list
        while (this.list.length > 0) {
          this.list.pop();
        }

        // Retrieve list
        await priceConfigurationList.getAll().then(async (list) => {
          list.forEach((priceConfiguration) => {
            this.reinsert(priceConfiguration);
          });

          // Activate priceConfiguration
          if (list.length > 0) {
            if (priceConfigurationList.active !== null) {
              this.activeID = priceConfigurationList.active;
            } else {
              await this.activate(list[0]);
            }
          }
        });

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        // Set user property
        if (this.activeID !== null) {
          analytics.setUserProperty("price_configuration_id", this.activeID);
        }

        return {
          active: this.activeID,
          items: this.list as PriceConfiguration[],
        };
      },
      async retrievePaginated(
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: PriceConfiguration[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as PriceConfiguration,
            ),
          };
        }

        // Retrieve list
        const items = await priceConfigurationList
          .getPaginated([], pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((priceConfiguration) => {
              this.reinsert(priceConfiguration);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: PriceConfiguration) => x.id),
          pagination: newPagination,
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items,
        };
      },
      async query(
        queries: Query[],
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: PriceConfiguration[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as PriceConfiguration,
            ),
          };
        }

        // Retrieve list
        const items = await priceConfigurationList
          .query(queries, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((priceConfiguration) => {
              this.reinsert(priceConfiguration);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: PriceConfiguration) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items: items,
        };
      },
      async search(
        query: string,
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: PriceConfiguration[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey =
          query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            items: cached.ids.map(
              (id: string) => this.get(id) as PriceConfiguration,
            ),
            pagination: cached.pagination.deepCopy(),
          };
        }

        // Retrieve list
        const items = await priceConfigurationList
          .search(query, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((priceConfiguration) => {
              this.reinsert(priceConfiguration);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: PriceConfiguration) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          items: items,
          pagination: newPagination,
        };
      },
      async update(priceConfiguration: PriceConfiguration): Promise<string> {
        // Update priceConfiguration
        await priceConfiguration.update();

        // Reinsert priceConfiguration
        this.reinsert(priceConfiguration);

        // Activate priceConfiguration
        if (this.active === null) {
          await this.activate(priceConfiguration);
        }

        this.emitStoreUpdateEvent();

        return priceConfiguration.id;
      },
    },
    getters: {
      active(): PriceConfiguration | null {
        if (this.activeID === null) {
          return null;
        }

        const priceConfiguration = (this.list as PriceConfiguration[]).find(
          (x: PriceConfiguration) => x.id === this.activeID,
        );
        if (priceConfiguration === undefined) {
          return null;
        }

        return priceConfiguration;
      },
      refreshRequired(): boolean {
        return (
          this.retrievalTimestamp === 0 ||
          this.retrievalTimestamp < Date.now() - 30 * 60000 ||
          this.list.length === 0
        );
      },
    },
  },
);

// Export a function to create a new instance of the store
export function createPriceConfigurationStore(): PriceConfigurationStore {
  const store = usePriceConfigurationStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as PriceConfigurationStore; // TODO: Fix this
}
